// ─── SurfacePreviewTeaser — hook d'acquisition PLG après un scan public ───────
// Après un scan, propose une découverte PASSIVE de la surface d'attaque et
// n'affiche que des compteurs agrégés (X actifs, Y exposés) + un CTA. Aucun
// détail d'actif n'est exposé côté anonyme : le détail vit dans l'espace client.
import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Radar, Globe, AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';
import SkuIcon from './SkuIcon';
import { surfacePreview, type SurfacePreview } from '../lib/api';

interface Props {
  domain: string;
  lang:   'fr' | 'en';
}

export default function SurfacePreviewTeaser({ domain, lang }: Props) {
  const t = (fr: string, en: string) => (lang === 'fr' ? fr : en);
  const navigate = useNavigate();

  const [preview, setPreview] = useState<SurfacePreview | null>(null);
  const [state, setState]     = useState<'loading' | 'done' | 'error'>('loading');
  const lastDomain            = useRef<string>('');

  useEffect(() => {
    if (!domain || domain === lastDomain.current) return;
    lastDomain.current = domain;
    let cancelled = false;
    setState('loading');
    surfacePreview(domain)
      .then(p => { if (!cancelled) { setPreview(p); setState('done'); } })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [domain]);

  if (state === 'error') return null;   // best-effort : silencieux en cas d'échec

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mt-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden"
      style={{ boxShadow: 'var(--shadow-card)' }}
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5">
        <SkuIcon color="#0d9488" size={44}><Radar size={22} className="text-[var(--color-accent-strong)]" /></SkuIcon>

        <div className="flex-1 min-w-0">
          <h3 className="font-display text-base font-bold text-[var(--color-ink)]">
            {t('Votre surface d’attaque externe', 'Your external attack surface')}
          </h3>

          {state === 'loading' ? (
            <p className="flex items-center gap-2 text-sm text-[var(--color-muted)] mt-1">
              <Loader2 size={14} className="animate-spin" />
              {t('Cartographie passive de vos actifs exposés…', 'Passively mapping your exposed assets…')}
            </p>
          ) : (
            <p className="text-sm text-[var(--color-body)] mt-1">
              {t('Nous avons repéré', 'We found')}{' '}
              <strong className="text-[var(--color-ink)]">{preview?.assets_count ?? 0}</strong>{' '}
              {t('actifs liés à', 'assets linked to')} <span className="font-mono">{preview?.seed_domain ?? domain}</span>,{' '}
              {t('dont', 'of which')}{' '}
              <strong className="text-[#be123c]">{preview?.exposed_count ?? 0}</strong>{' '}
              {t('exposés sur Internet.', 'exposed to the Internet.')}
            </p>
          )}

          {state === 'done' && (
            <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-[var(--color-muted)]">
              <span className="inline-flex items-center gap-1"><Globe size={12} className="text-indigo-500" /> {t('Sous-domaines & IP', 'Subdomains & IPs')}</span>
              <span className="inline-flex items-center gap-1"><AlertTriangle size={12} className="text-[#be123c]" /> {t('Risques de takeover détectés', 'Takeover risks detected')}</span>
            </div>
          )}
        </div>

        <button
          onClick={() => navigate('/register')}
          className="sku-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap self-stretch sm:self-auto justify-center"
        >
          {t('Voir le détail', 'See the details')}
          <ArrowRight size={15} />
        </button>
      </div>
    </motion.div>
  );
}
